// frontend/src/pages/Faqs.js
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FileText, HelpCircle, Lock, MessageCircle } from "lucide-react";

export default function FaqPage() {
  const { t } = useTranslation();

  // 定義各分類 FAQ 的 i18n key 陣列
  const commonQuestions = [
    { question: "faqs.commonQuestions.q1.question", answer: "faqs.commonQuestions.q1.answer" },
    { question: "faqs.commonQuestions.q2.question", answer: "faqs.commonQuestions.q2.answer" },
    { question: "faqs.commonQuestions.q3.question", answer: "faqs.commonQuestions.q3.answer" },
    { question: "faqs.commonQuestions.q4.question", answer: "faqs.commonQuestions.q4.answer" },
    { question: "faqs.commonQuestions.q5.question", answer: "faqs.commonQuestions.q5.answer" },
  ];

  const caseReport = [
    { question: "faqs.caseReport.q1.question", answer: "faqs.caseReport.q1.answer" },
    { question: "faqs.caseReport.q2.question", answer: "faqs.caseReport.q2.answer" },
    { question: "faqs.caseReport.q3.question", answer: "faqs.caseReport.q3.answer" },
    { question: "faqs.caseReport.q4.question", answer: "faqs.caseReport.q4.answer" },
    { question: "faqs.caseReport.q5.question", answer: "faqs.caseReport.q5.answer" },
  ];

  const fraudSupport = [
    { question: "faqs.fraudSupport.q1.question", answer: "faqs.fraudSupport.q1.answer" },
    { question: "faqs.fraudSupport.q2.question", answer: "faqs.fraudSupport.q2.answer" },
    { question: "faqs.fraudSupport.q3.question", answer: "faqs.fraudSupport.q3.answer" },
    { question: "faqs.fraudSupport.q4.question", answer: "faqs.fraudSupport.q4.answer" },
    { question: "faqs.fraudSupport.q5.question", answer: "faqs.fraudSupport.q5.answer" },
  ];

  // 搜尋狀態
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = () => {
    setHasSearched(true);
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return;
    }
    const allFaqs = [...commonQuestions, ...caseReport, ...fraudSupport];
    const results = allFaqs.filter((faq) =>
      t(faq.question).toLowerCase().includes(searchTerm.toLowerCase()) ||
      t(faq.answer).toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeInOut" },
    },
  };

  const [searchRef, searchInView] = useInView({ threshold: 0.2 });
  const [categoriesRef, categoriesInView] = useInView({ threshold: 0.2 });

  // 定義分類按鈕資料
  const categories = [
    {
      title: t("faqs.commonQuestions.title"),
      icon: FileText,
      image: "/images/button_faq01.png",
      link: "/faqs/common-questions",
    },
    {
      title: t("faqs.caseReport.title"),
      icon: HelpCircle,
      image: "/images/button_faq02.png",
      link: "/faqs/case-report",
    },
    {
      title: t("faqs.fraudSupport.title"),
      icon: MessageCircle,
      image: "/images/button_faq03.png",
      link: "/faqs/fraud-support",
    },
    {
      title: t("faqs.privacyPolicy.title"),
      icon: Lock,
      image: "/images/button_faq04.png",
      link: "/privacy-policy",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800 min-h-screen text-gray-800 dark:text-gray-200 font-sans overflow-hidden">
      {/* Banner */}
      <div
        className="w-full h-64 bg-cover bg-center flex items-center justify-center text-white text-3xl font-bold"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        {t("faqs.title")}
      </div>

      <div className="container mx-auto px-6 py-24 relative">
        {/* 搜尋區塊 */}
        <motion.div
          ref={searchRef}
          initial="hidden"
          animate={searchInView ? "visible" : "hidden"}
          variants={fadeIn}
          className="max-w-3xl mx-auto mb-24"
        >
          <p className="text-lg text-center leading-relaxed text-gray-600 dark:text-gray-400 mb-6">
            {t("faqs.searchDescription")}
            <Link to="/contact">
              <span className="text-blue-500 hover:text-blue-600 cursor-pointer">
                {t("faqs.contact")}
              </span>
            </Link>
            {t("faqs.searchContact")}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={t("faqs.searchPlaceholder")}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition duration-300"
              onClick={handleSearch}
            >
              {t("faqs.searchButton")}
            </button>
          </div>
          {hasSearched && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold mb-4">{t("faqs.searchResults")}</h3>
              {searchResults.length > 0 ? (
                <div className="space-y-4">
                  {searchResults.map((faq, index) => (
                    <div
                      key={index}
                      className="p-4 border border-gray-300 dark:border-gray-700 rounded-lg"
                    >
                      <h4 className="font-semibold">{t(faq.question)}</h4>
                      <p className="mt-2 text-gray-600 dark:text-gray-400">
                        {t(faq.answer)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  {t("faqs.noResults")}
                </p>
              )}
            </div>
          )}
        </motion.div>

        {/* FAQ 分類按鈕 */}
        <motion.div
          ref={categoriesRef}
          initial="hidden"
          animate={categoriesInView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <h2 className="text-4xl font-bold text-center mb-12 tracking-tight leading-tight">
            {t("faqs.categoriesTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {categories.map((category) => (
              <Link key={category.title} to={category.link}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="relative rounded-2xl shadow-lg overflow-hidden group w-full h-48 md:h-56 lg:h-64"
                  style={{
                    backgroundImage: `url(${category.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="absolute top-4 left-4 flex items-center space-x-2">
                    <category.icon className="w-8 h-8 text-black" />
                    <h3 className="text-xl md:text-2xl font-semibold text-black">
                      {category.title}
                    </h3>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
